import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Trash2, Download, LogOut, Star, ArrowLeft, Pencil, Plus, Pause, Play,
  FileText, Inbox, Newspaper, CalendarDays, SlidersHorizontal, Trophy, Table, TrendingUp,
  BookOpen, ListChecks, Brain,
} from "lucide-react";
import { Logo } from "../components/Logo";
import api, { formatApiError } from "../lib/api";
import { DEFAULT_CONTENT } from "../lib/useContent";

const inputCls =
  "w-full rounded-2xl border-2 border-foreground/10 bg-background px-4 py-3 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf";
const labelCls = "text-sm font-semibold";
const cardCls = "rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm";

const TABS = [
  { id: "content", label: "Site content", icon: SlidersHorizontal, testid: "admin-tab-content" },
  { id: "news", label: "Headlines", icon: Newspaper, testid: "admin-tab-news" },
  { id: "blogs", label: "Research blogs", icon: BookOpen, testid: "admin-tab-blogs" },
  { id: "survey-edit", label: "Survey editor", icon: Pencil, testid: "admin-tab-survey-editor" },
  { id: "voices", label: "Voices", icon: ListChecks, testid: "admin-tab-quotes" },
  { id: "games", label: "Big games", icon: CalendarDays, testid: "admin-tab-games" },
  { id: "tournaments", label: "Tournaments", icon: Trophy, testid: "admin-tab-tournaments" },
  { id: "table", label: "Points table", icon: Table, testid: "admin-tab-points-table" },
  { id: "stats", label: "Top stats", icon: TrendingUp, testid: "admin-tab-leading-stats" },
  { id: "data", label: "Survey data", icon: FileText, testid: "admin-tab-survey-data" },
  { id: "quiz", label: "Daily quiz", icon: Brain, testid: "admin-tab-quiz" },
  { id: "messages", label: "Messages", icon: Inbox, testid: "admin-tab-messages" },
];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("sidelines_token"));
  if (!token) {
    return <LoginScreen onLogin={() => setToken(localStorage.getItem("sidelines_token"))} />;
  }
  return (
    <OwnerRoom
      onLogout={() => {
        localStorage.removeItem("sidelines_token");
        setToken(null);
      }}
    />
  );
};

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sidelines_token", data.access_token);
      onLogin();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-background grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl bg-card border border-foreground/10 p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <Logo className="h-11 w-11" />
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Owner's room</h1>
            <p className="text-sm text-muted-foreground">Only you should know this one.</p>
          </div>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="admin-email" className={labelCls}>Email</label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your admin email"
              autoComplete="username"
              className={`mt-2 ${inputCls}`}
            />
          </div>
          <div>
            <label htmlFor="admin-password" className={labelCls}>Password</label>
            <input
              id="admin-password"
              type="password"
              data-testid="admin-passkey-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="your password"
              autoComplete="current-password"
              className={`mt-2 ${inputCls}`}
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-flame font-medium">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Let me in"}
          </button>
          <Link to="/" className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to the site
          </Link>
        </div>
      </form>
    </div>
  );
};

const OwnerRoom = ({ onLogout }) => {
  const [tab, setTab] = useState("content");
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [games, setGames] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [standings, setStandings] = useState([]);
  const [leadingstats, setLeadingstats] = useState([]);
  const [content, setContent] = useState({ ...DEFAULT_CONTENT });

  useEffect(() => {
    const load = async () => {
      try {
        const [n, b, r, resp, m, c, g, t, s, st] = await Promise.all([
          api.get("/news"),
          api.get("/blogs"),
          api.get("/reviews"),
          api.get("/admin/responses"),
          api.get("/admin/messages"),
          api.get("/content"),
          api.get("/biggames"),
          api.get("/tournaments"),
          api.get("/standings"),
          api.get("/leadingstats"),
        ]);
        setNews(n.data);
        setBlogs(b.data);
        setReviews(r.data);
        setBundles(resp.data);
        setMessages(m.data);
        setContent({ ...DEFAULT_CONTENT, ...c.data });
        setGames(g.data);
        setTournaments(t.data);
        setStandings(s.data);
        setLeadingstats(st.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    onLogout();
  };

  return (
    <div data-testid="admin-drawer" className="min-h-screen bg-cream dark:bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-cream/85 dark:bg-background/85 border-b border-foreground/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-display font-bold leading-none">Owner's room</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Sidelines admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-2 border-foreground/10 text-sm font-semibold hover:bg-muted transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back to site</span>
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-flame text-white text-sm font-semibold hover:brightness-110 transition-all"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">Run the site from here</h1>
        <p className="mt-2 text-muted-foreground">
          Everything you post or change shows up on the site immediately. This is your website —
          edit it as often as you like.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-xs font-semibold uppercase tracking-wide transition-all ${
                tab === t.id
                  ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf"
                  : "border-foreground/10 bg-card hover:border-pitch/40 dark:hover:border-turf/50"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-12 grid place-items-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="mt-6">
            {tab === "content" && <ContentTab content={content} />}
            {tab === "news" && <NewsTab news={news} setNews={setNews} />}
            {tab === "blogs" && <BlogsTab blogs={blogs} setBlogs={setBlogs} />}
            {tab === "survey-edit" && <SurveyTab />}
            {tab === "voices" && <VoicesTab reviews={reviews} setReviews={setReviews} />}
            {tab === "games" && <GamesTab games={games} setGames={setGames} />}
            {tab === "tournaments" && <TournamentsTab tournaments={tournaments} setTournaments={setTournaments} />}
            {tab === "table" && <StandingsTab standings={standings} setStandings={setStandings} />}
            {tab === "stats" && <StatsTab leadingstats={leadingstats} setLeadingstats={setLeadingstats} standings={standings} />}
            {tab === "data" && <DataTab bundles={bundles} />}
            {tab === "quiz" && <QuizTab />}
            {tab === "messages" && <MessagesTab messages={messages} setMessages={setMessages} />}
          </div>
        )}
      </main>
    </div>
  );
};

const ContentTab = ({ content }) => {
  const [form, setForm] = useState(content);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", form);
      toast.success("Saved — live on the site now");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    { key: "founder_name", label: "Your name (shown on the founder note)" },
    { key: "contact_phone", label: "Your phone number (Talk to us page)" },
    { key: "contact_email", label: "Contact email" },
    { key: "contact_instagram", label: "Instagram" },
    { key: "contact_location", label: "Where you're based" },
  ];

  return (
    <div className={cardCls}>
      <h2 className="font-display text-xl font-bold">Site content</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Your name, number and contact details — everything here updates the public pages the
        moment you save.
      </p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label className={labelCls}>{f.label}</label>
            <input value={form[f.key] || ""} onChange={set(f.key)} className={`mt-2 ${inputCls}`} data-testid={`admin-content-${f.key}`} />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <label className={labelCls}>Founder note (the "Hi! I'm 15" card on the home page)</label>
        <textarea rows={4} value={form.founder_note || ""} onChange={set("founder_note")} className={`mt-2 ${inputCls}`} data-testid="admin-content-founder_note" />
      </div>
      <div className="mt-4">
        <label className={labelCls}>Talk to us page intro</label>
        <textarea rows={3} value={form.talk_intro || ""} onChange={set("talk_intro")} className={`mt-2 ${inputCls}`} data-testid="admin-content-talk_intro" />
      </div>
      <button
        onClick={save}
        data-testid="admin-save-content-btn"
        disabled={busy}
        className="mt-6 h-12 px-8 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Save changes"}
      </button>
    </div>
  );
};

const NewsTab = ({ news, setNews }) => {
  const [f, setF] = useState({ title: "", tag: "Morbi news", body: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.title.trim() || !f.body.trim()) {
      toast.error("Give it a headline and a bit of story.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/news", f);
      setNews((xs) => [data, ...xs]);
      setF({ title: "", tag: f.tag, body: "" });
      toast.success("Headline posted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/news/${id}`);
      setNews((xs) => xs.filter((x) => x.id !== id));
      toast.success("Headline deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Post a headline</h2>
        <div className="mt-4 grid sm:grid-cols-4 gap-4">
          <div className="sm:col-span-3">
            <label className={labelCls}>Headline</label>
            <input value={f.title} onChange={set("title")} placeholder="e.g. Floodlights approved for the school pitch" className={`mt-2 ${inputCls}`} data-testid="admin-news-title" />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <input value={f.tag} onChange={set("tag")} list="news-tags" className={`mt-2 ${inputCls}`} data-testid="admin-news-tag" />
            <datalist id="news-tags">
              <option value="Morbi news" />
              <option value="Local football" />
              <option value="Voices" />
              <option value="Match report" />
            </datalist>
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>The story</label>
          <textarea rows={4} value={f.body} onChange={set("body")} placeholder="What happened, who told you, and why it matters to young players…" className={`mt-2 ${inputCls}`} data-testid="admin-news-body" />
        </div>
        <button onClick={add} data-testid="admin-add-news-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Post headline"}
        </button>
      </div>

      <div className="space-y-3">
        {news.length === 0 && <p className="text-sm text-muted-foreground">No headlines yet.</p>}
        {news.map((n) => (
          <div key={n.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame">{n.tag}</span>
              <p className="font-semibold text-sm mt-1">{n.title}</p>
            </div>
            <button onClick={() => del(n.id)} data-testid="admin-delete-news" aria-label="Delete headline" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlogsTab = ({ blogs, setBlogs }) => {
  const blank = { title: "", tag: "Findings", body: "" };
  const [f, setF] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [editF, setEditF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.title.trim() || !f.body.trim()) {
      toast.error("A blog post needs a title and its story.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blogs", f);
      setBlogs((xs) => [data, ...xs]);
      setF(blank);
      toast.success("Post published to the Research page");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/blogs/${id}`);
      setBlogs((xs) => xs.filter((x) => x.id !== id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditF({ title: b.title, tag: b.tag, body: b.body });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/blogs/${editingId}`, editF);
      setBlogs((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      setEditingId(null);
      setEditF(null);
      toast.success("Post updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Write a research blog post</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your write-ups live on the Research page — findings, diary entries, interviews, method.
        </p>
        <div className="mt-4 grid sm:grid-cols-4 gap-4">
          <div className="sm:col-span-3">
            <label className={labelCls}>Title</label>
            <input value={f.title} onChange={set("title")} placeholder="e.g. What the first 20 answers told me" className={`mt-2 ${inputCls}`} data-testid="admin-blog-title" />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <input value={f.tag} onChange={set("tag")} list="blog-tags" className={`mt-2 ${inputCls}`} data-testid="admin-blog-tag" />
            <datalist id="blog-tags">
              <option value="Findings" />
              <option value="Method" />
              <option value="Diary" />
              <option value="Interview" />
            </datalist>
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>The post</label>
          <textarea rows={6} value={f.body} onChange={set("body")} placeholder="Write as many paragraphs as you like — start each new paragraph on a new line…" className={`mt-2 ${inputCls}`} data-testid="admin-blog-body" />
        </div>
        <button onClick={add} data-testid="admin-add-blog-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Publish post"}
        </button>
      </div>

      <div className="space-y-3">
        {blogs.length === 0 && <p className="text-sm text-muted-foreground">No posts yet.</p>}
        {blogs.map((b) => (
          <div key={b.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4" data-testid="admin-blog-row">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame">{b.tag}</span>
                <p className="font-semibold text-sm mt-1">{b.title}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => (editingId === b.id ? setEditingId(null) : startEdit(b))} data-testid="admin-edit-blog" aria-label="Edit post" className="h-9 w-9 grid place-items-center rounded-full hover:bg-sage/50 dark:hover:bg-turf/15 hover:text-pitch dark:hover:text-turf transition-colors">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => del(b.id)} data-testid="admin-delete-blog" aria-label="Delete post" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {editingId === b.id && editF && (
              <div className="mt-4 rounded-2xl border-2 border-foreground/10 bg-background p-4 space-y-3" data-testid="admin-blog-editor">
                <input value={editF.title} onChange={(e) => setEditF((x) => ({ ...x, title: e.target.value }))} className={inputCls} data-testid="admin-edit-blog-title" />
                <input value={editF.tag} onChange={(e) => setEditF((x) => ({ ...x, tag: e.target.value }))} className={inputCls} data-testid="admin-edit-blog-tag" />
                <textarea rows={6} value={editF.body} onChange={(e) => setEditF((x) => ({ ...x, body: e.target.value }))} className={inputCls} data-testid="admin-edit-blog-body" />
                <div className="flex gap-3">
                  <button onClick={saveEdit} data-testid="admin-save-blog-btn" disabled={busy} className="h-11 px-6 rounded-full bg-pitch text-cream text-sm font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Save"}
                  </button>
                  <button onClick={() => setEditingId(null)} className="h-11 px-6 rounded-full border-2 border-foreground/10 text-sm font-semibold hover:bg-muted transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SurveyTab = () => {
  const [survey, setSurvey] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .get("/admin/surveys")
      .then((r) => setSurvey(r.data[0] || null))
      .catch(() => setSurvey(null));
  };

  useEffect(() => {
    load();
  }, []);

  if (survey === undefined) {
    return (
      <div className="grid place-items-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (survey === null) {
    return (
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">No survey yet</h2>
        <p className="mt-1 text-sm text-muted-foreground">Create one — questions can be choice (pick an option) or text (write an answer).</p>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/surveys", { title: "My survey", intro: "", questions: [] });
              setSurvey(data);
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          data-testid="admin-create-survey-btn"
          className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background"
        >
          Create survey
        </button>
      </div>
    );
  }

  const toggleSurvey = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/surveys/${survey.id}/toggle`);
      setSurvey((s) => ({ ...s, active: data.active }));
      toast.success(data.active ? "Survey is live again" : "Survey paused — the home page shows a friendly break notice");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setQ = (i, patch) =>
    setSurvey((s) => ({ ...s, questions: s.questions.map((q, qi) => (qi === i ? { ...q, ...patch } : q)) }));

  const addQuestion = () =>
    setSurvey((s) => ({ ...s, questions: [...s.questions, { text: "", type: "choice", options: ["", ""] }] }));

  const removeQuestion = (i) =>
    setSurvey((s) => ({ ...s, questions: s.questions.filter((_, qi) => qi !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/surveys/${survey.id}`, {
        title: survey.title,
        intro: survey.intro,
        questions: survey.questions,
      });
      setSurvey(data);
      toast.success("Survey saved — the home page updates right away");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold">Edit the survey</h2>
          <div className="flex items-center gap-3">
            <span className={`inline-block rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] font-semibold ${survey.active ? "bg-turf/20 text-pitch dark:text-turf" : "bg-muted text-muted-foreground"}`}>
              {survey.active ? "Live" : "Paused"}
            </span>
            <button onClick={toggleSurvey} data-testid="admin-toggle-survey-btn" disabled={busy} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-2 border-pitch/25 dark:border-turf/40 text-xs font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf transition-all">
              {survey.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {survey.active ? "Pause survey" : "Put it live"}
            </button>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          This is the survey on the home page. Choice questions get buttons; text questions get a
          box to write in.
        </p>
        <p className="mt-2 text-xs text-flame font-medium">
          Heads-up: if you rename or replace a question after people have answered, their old
          answers may not line up with the new wording.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Survey title</label>
            <input value={survey.title} onChange={(e) => setSurvey((s) => ({ ...s, title: e.target.value }))} className={`mt-2 ${inputCls}`} data-testid="admin-survey-title" />
          </div>
          <div>
            <label className={labelCls}>Intro line</label>
            <input value={survey.intro || ""} onChange={(e) => setSurvey((s) => ({ ...s, intro: e.target.value }))} className={`mt-2 ${inputCls}`} data-testid="admin-survey-intro" />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {survey.questions.map((q, i) => (
            <div key={q.id || `new-${i}`} className="rounded-2xl border-2 border-foreground/10 bg-background p-4" data-testid="admin-survey-question">
              <div className="flex items-start justify-between gap-3">
                <span className="font-mono text-xs text-flame mt-3">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <input
                    value={q.text}
                    onChange={(e) => setQ(i, { text: e.target.value })}
                    placeholder={`Question ${i + 1} — what do you want to ask?`}
                    className={inputCls}
                    data-testid={`admin-survey-q-text-${i}`}
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => setQ(i, { type: "choice", options: q.options?.length ? q.options : ["", ""] })}
                      data-testid={`admin-survey-type-choice-${i}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${q.type === "choice" ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf" : "border-foreground/10"}`}
                    >
                      Pick an option
                    </button>
                    <button
                      onClick={() => setQ(i, { type: "text" })}
                      data-testid={`admin-survey-type-text-${i}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${q.type === "text" ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf" : "border-foreground/10"}`}
                    >
                      Write an answer
                    </button>
                  </div>
                  {q.type === "choice" && (
                    <div className="mt-3 space-y-2">
                      {(q.options || []).map((o, oi) => (
                        <div key={oi} className="flex gap-2">
                          <input
                            value={o}
                            onChange={(e) => setQ(i, { options: q.options.map((x, xi) => (xi === oi ? e.target.value : x)) })}
                            placeholder={`Option ${oi + 1}`}
                            className={inputCls}
                            data-testid={`admin-survey-q-${i}-opt-${oi}`}
                          />
                          {(q.options || []).length > 2 && (
                            <button onClick={() => setQ(i, { options: q.options.filter((_, xi) => xi !== oi) })} aria-label="Remove option" className="h-11 w-11 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      {(q.options || []).length < 8 && (
                        <button onClick={() => setQ(i, { options: [...(q.options || []), ""] })} className="text-xs font-semibold text-pitch dark:text-turf" data-testid={`admin-survey-q-${i}-addopt`}>
                          + Add option
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => removeQuestion(i)} aria-label="Remove question" data-testid={`admin-survey-q-remove-${i}`} className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0 mt-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-4">
          <button onClick={addQuestion} data-testid="admin-add-question-btn" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border-2 border-pitch/25 dark:border-turf/40 text-sm font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf transition-all">
            <Plus className="h-4 w-4" /> Add a question
          </button>
          <button onClick={save} data-testid="admin-save-survey-btn" disabled={busy} className="inline-flex items-center gap-1.5 h-11 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save survey"}
          </button>
        </div>
      </div>
    </div>
  );
};

const VoicesTab = ({ reviews, setReviews }) => {
  const [f, setF] = useState({ author: "", role: "", text: "", rating: 5 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.author.trim() || !f.text.trim()) {
      toast.error("A quote needs who said it and what they said.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reviews", f);
      setReviews((rs) => [data, ...rs]);
      setF({ author: "", role: "", text: "", rating: 5 });
      toast.success("Quote added to the wall");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Quote removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Add a quote people told you</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Visitor quotes pass through the bad-word filter automatically — anything rude you spot
          here, delete it.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Who said it</label>
            <input value={f.author} onChange={set("author")} placeholder="e.g. Local teen, 15" className={`mt-2 ${inputCls}`} data-testid="admin-quote-author" />
          </div>
          <div>
            <label className={labelCls}>Their role (optional)</label>
            <input value={f.role} onChange={set("role")} placeholder="e.g. Parent of two" className={`mt-2 ${inputCls}`} data-testid="admin-quote-role" />
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>The quote</label>
          <textarea rows={3} value={f.text} onChange={set("text")} placeholder='"I watch every week but trials felt too scary…"' className={`mt-2 ${inputCls}`} data-testid="admin-quote-text" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className={labelCls}>Stars:</span>
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => setF((x) => ({ ...x, rating: s }))} aria-label={`Rate ${s} stars`} data-testid={`admin-quote-star-${s}`}>
              <Star className={`h-6 w-6 ${s <= f.rating ? "fill-flame text-flame" : "text-foreground/20"}`} />
            </button>
          ))}
        </div>
        <button onClick={add} data-testid="admin-add-quote-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Add quote"}
        </button>
      </div>

      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm italic">"{r.text}"</p>
              <p className="text-xs text-muted-foreground mt-1">
                {r.author} · {r.role || "no role"} · {r.source === "owner" ? "added by you" : "sent by a visitor"}
              </p>
            </div>
            <button onClick={() => del(r.id)} data-testid="admin-delete-quote" aria-label="Delete quote" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const GamesTab = ({ games, setGames }) => {
  const [f, setF] = useState({ title: "", date_label: "", competition: "", watch_on: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.title.trim()) {
      toast.error("Which game is it? Give it a title.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/biggames", f);
      setGames((gs) => [data, ...gs]);
      setF({ title: "", date_label: "", competition: "", watch_on: "", note: "" });
      toast.success("Big game added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/biggames/${id}`);
      setGames((gs) => gs.filter((g) => g.id !== id));
      toast.success("Game removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Add a big game</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>The game</label>
            <input value={f.title} onChange={set("title")} placeholder="e.g. Morbi derby — youth cup final" className={`mt-2 ${inputCls}`} data-testid="admin-game-title" />
          </div>
          <div>
            <label className={labelCls}>When (in your words)</label>
            <input value={f.date_label} onChange={set("date_label")} placeholder="e.g. Saturday, 3pm" className={`mt-2 ${inputCls}`} data-testid="admin-game-date" />
          </div>
          <div>
            <label className={labelCls}>Competition</label>
            <input value={f.competition} onChange={set("competition")} placeholder="e.g. Local youth cup" className={`mt-2 ${inputCls}`} data-testid="admin-game-competition" />
          </div>
          <div>
            <label className={labelCls}>Where to watch</label>
            <input value={f.watch_on} onChange={set("watch_on")} placeholder="e.g. The rec ground, £1 entry" className={`mt-2 ${inputCls}`} data-testid="admin-game-watch" />
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>Why it matters (optional)</label>
          <textarea rows={2} value={f.note} onChange={set("note")} placeholder="Half the school goes to this one…" className={`mt-2 ${inputCls}`} data-testid="admin-game-note" />
        </div>
        <button onClick={add} data-testid="admin-add-game-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Add game"}
        </button>
      </div>

      <div className="space-y-3">
        {games.length === 0 && <p className="text-sm text-muted-foreground">No big games listed yet.</p>}
        {games.map((g) => (
          <div key={g.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-sm">{g.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {[g.date_label, g.competition, g.watch_on].filter(Boolean).join(" · ") || "no details"}
              </p>
            </div>
            <button onClick={() => del(g.id)} data-testid="admin-delete-game" aria-label="Delete game" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TournamentsTab = ({ tournaments, setTournaments }) => {
  const [f, setF] = useState({ title: "", date_label: "", venue: "", entry_info: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.title.trim()) {
      toast.error("Give the tournament a title.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tournaments", f);
      setTournaments((ts) => [data, ...ts]);
      setF({ title: "", date_label: "", venue: "", entry_info: "", note: "" });
      toast.success("Tournament added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/tournaments/${id}`);
      setTournaments((ts) => ts.filter((t) => t.id !== id));
      toast.success("Tournament removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Add an upcoming tournament</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tournament</label>
            <input value={f.title} onChange={set("title")} placeholder="e.g. Summer 5-a-side cup" className={`mt-2 ${inputCls}`} data-testid="admin-tournament-title" />
          </div>
          <div>
            <label className={labelCls}>When (in your words)</label>
            <input value={f.date_label} onChange={set("date_label")} placeholder="e.g. June — weekends" className={`mt-2 ${inputCls}`} data-testid="admin-tournament-date" />
          </div>
          <div>
            <label className={labelCls}>Where</label>
            <input value={f.venue} onChange={set("venue")} placeholder="e.g. The rec ground" className={`mt-2 ${inputCls}`} data-testid="admin-tournament-venue" />
          </div>
          <div>
            <label className={labelCls}>Entry info</label>
            <input value={f.entry_info} onChange={set("entry_info")} placeholder="e.g. Free entry, squads of 6" className={`mt-2 ${inputCls}`} data-testid="admin-tournament-entry" />
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>Extra note (optional)</label>
          <textarea rows={2} value={f.note} onChange={set("note")} placeholder="Sign-up closes Friday…" className={`mt-2 ${inputCls}`} data-testid="admin-tournament-note" />
        </div>
        <button onClick={add} data-testid="admin-add-tournament-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Add tournament"}
        </button>
      </div>

      <div className="space-y-3">
        {tournaments.length === 0 && <p className="text-sm text-muted-foreground">No tournaments listed yet.</p>}
        {tournaments.map((t) => (
          <div key={t.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-sm">{t.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {[t.date_label, t.venue, t.entry_info].filter(Boolean).join(" · ") || "no details"}
              </p>
            </div>
            <button onClick={() => del(t.id)} data-testid="admin-delete-tournament" aria-label="Delete tournament" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const numField = (val, onChange, testid, placeholder) => (
  <input
    type="number"
    value={val}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    placeholder={placeholder}
    className="w-full rounded-xl border-2 border-foreground/10 bg-background px-3 py-2.5 text-sm text-center focus:outline-none focus:border-pitch dark:focus:border-turf"
    data-testid={testid}
  />
);

const StandingsTab = ({ standings, setStandings }) => {
  const leagues = Array.from(new Set(standings.map((s) => s.league || "Boys")));
  const leagueOptions = Array.from(new Set([...leagues, "Boys", "Girls"]));
  const blank = (league) => ({ team: "", league, played: 0, won: 0, drawn: 0, lost: 0, points: 0 });
  const [f, setF] = useState(blank("Boys"));
  const [editingId, setEditingId] = useState(null);
  const [editF, setEditF] = useState(null);
  const [newLeague, setNewLeague] = useState("");
  const [showNewLeague, setShowNewLeague] = useState(false);
  const [busy, setBusy] = useState(false);

  const resort = (rows) =>
    [...rows].sort(
      (a, b) =>
        (a.league || "Boys").localeCompare(b.league || "Boys") ||
        b.points - a.points ||
        b.won - a.won ||
        a.team.localeCompare(b.team)
    );

  const activeLeague = showNewLeague ? newLeague.trim() : f.league;

  const add = async () => {
    if (!f.team.trim()) {
      toast.error("Give the team a name.");
      return;
    }
    if (!activeLeague) {
      toast.error("Pick an existing table or name a new one.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/standings", { ...f, league: activeLeague });
      setStandings((ss) => resort([...ss, data]));
      setF((x) => ({ ...blank(activeLeague), played: x.played }));
      if (showNewLeague) {
        setShowNewLeague(false);
        setNewLeague("");
      }
      toast.success(`Team added to the "${activeLeague}" table`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/standings/${id}`);
      setStandings((ss) => ss.filter((s) => s.id !== id));
      toast.success("Team removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditF({ team: s.team, league: s.league || "Boys", played: s.played, won: s.won, drawn: s.drawn, lost: s.lost, points: s.points });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/standings/${editingId}`, editF);
      setStandings((ss) => resort(ss.map((s) => (s.id === data.id ? data : s))));
      setEditingId(null);
      setEditF(null);
      toast.success("Table updated and re-sorted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Points tables — girls & boys</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Make as many tables as you want (Boys, Girls, U14s…): hit "New table", name it, then add
          teams into it. Each table sorts itself by points automatically.
        </p>

        <div className="mt-5 space-y-3">
          <div>
            <label className={labelCls}>Team name</label>
            <input value={f.team} onChange={(e) => setF((x) => ({ ...x, team: e.target.value }))} placeholder="e.g. Morbi Rovers" className={`mt-2 ${inputCls}`} data-testid="admin-standing-team" />
          </div>

          <div>
            <label className={labelCls}>Which table?</label>
            {!showNewLeague ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {leagueOptions.map((lg) => (
                  <button
                    key={lg}
                    onClick={() => setF((x) => ({ ...x, league: lg }))}
                    data-testid={`admin-league-pick-${lg.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-4 py-2 rounded-full border-2 text-xs font-semibold transition-all ${
                      f.league === lg
                        ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf"
                        : "border-foreground/10 bg-background hover:border-pitch/40 dark:hover:border-turf/50"
                    }`}
                  >
                    {lg}
                  </button>
                ))}
                <button
                  onClick={() => setShowNewLeague(true)}
                  data-testid="admin-new-league-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-flame text-white text-xs font-semibold hover:brightness-110 transition-all"
                >
                  + New table
                </button>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <input
                  value={newLeague}
                  onChange={(e) => setNewLeague(e.target.value)}
                  placeholder="Name the table, e.g. Girls U14 league"
                  className={inputCls}
                  data-testid="admin-new-league-input"
                  autoFocus
                />
                <button
                  onClick={() => {
                    if (!newLeague.trim()) return toast.error("Type a name for the table.");
                    setShowNewLeague(false);
                    setF((x) => ({ ...x, league: newLeague.trim() }));
                  }}
                  data-testid="admin-new-league-save-btn"
                  className="h-11 px-5 rounded-full bg-pitch text-cream text-sm font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background shrink-0"
                >
                  Create
                </button>
                <button
                  onClick={() => setShowNewLeague(false)}
                  className="h-11 px-5 rounded-full border-2 border-foreground/10 text-sm font-semibold hover:bg-muted transition-colors shrink-0"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            {["played", "won", "drawn", "lost", "points"].map((k) => (
              <div key={k} className="flex-1 min-w-[80px]">
                <label className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{k === "played" ? "Played" : k === "points" ? "Points" : k}</label>
                {numField(f[k], (v) => setF((x) => ({ ...x, [k]: v })), `admin-standing-${k}`, k)}
              </div>
            ))}
          </div>
          <button onClick={add} data-testid="admin-add-standing-btn" disabled={busy} className="h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
            {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : `Add team${activeLeague ? ` to "${activeLeague}"` : ""}`}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {standings.length === 0 && <p className="text-sm text-muted-foreground">No teams yet — the tables are empty.</p>}
        {standings.map((s, i) => {
          const leagueRows = standings.filter((x) => (x.league || "Boys") === (s.league || "Boys"));
          const pos = leagueRows.findIndex((x) => x.id === s.id) + 1;
          return (
            <div key={s.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4" data-testid="admin-standing-row">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-7 w-7 rounded-full grid place-items-center font-mono text-xs shrink-0 ${pos === 1 ? "bg-flame text-white" : "bg-muted text-muted-foreground"}`}>
                    {pos}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {s.team} <span className="font-mono text-[10px] uppercase tracking-wide text-flame">· {s.league || "Boys"}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.played}P · {s.won}W · {s.drawn}D · {s.lost}L · <span className="font-bold text-pitch dark:text-turf">{s.points} pts</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => (editingId === s.id ? setEditingId(null) : startEdit(s))} data-testid="admin-edit-standing" aria-label="Edit team" className="h-9 w-9 grid place-items-center rounded-full hover:bg-sage/50 dark:hover:bg-turf/15 hover:text-pitch dark:hover:text-turf transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(s.id)} data-testid="admin-delete-standing" aria-label="Delete team" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {editingId === s.id && editF && (
                <div className="mt-4 rounded-2xl border-2 border-foreground/10 bg-background p-4" data-testid="admin-standing-editor">
                  <div className="mb-3 grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Team name</label>
                      <input value={editF.team} onChange={(e) => setEditF((x) => ({ ...x, team: e.target.value }))} className={`mt-1.5 ${inputCls}`} data-testid="admin-edit-standing-team" />
                    </div>
                    <div>
                      <label className={labelCls}>Table (league)</label>
                      <input value={editF.league} onChange={(e) => setEditF((x) => ({ ...x, league: e.target.value }))} list="edit-league-list" className={`mt-1.5 ${inputCls}`} data-testid="admin-edit-standing-league" />
                      <datalist id="edit-league-list">
                        {leagueOptions.map((lg) => (
                          <option key={lg} value={lg} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {["played", "won", "drawn", "lost", "points"].map((k) => (
                      <div key={k} className="flex-1 min-w-[80px]">
                        <label className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{k === "played" ? "Played" : k === "points" ? "Points" : k}</label>
                        {numField(editF[k], (v) => setEditF((x) => ({ ...x, [k]: v })), `admin-edit-standing-${k}`, k)}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={saveEdit} data-testid="admin-save-standing-btn" disabled={busy} className="h-11 px-6 rounded-full bg-pitch text-cream text-sm font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Save"}
                    </button>
                    <button onClick={() => setEditingId(null)} className="h-11 px-6 rounded-full border-2 border-foreground/10 text-sm font-semibold hover:bg-muted transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatsTab = ({ leadingstats, setLeadingstats, standings }) => {
  const blank = { player: "", team: "", stat_type: "Goals", value: 0 };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    if (!f.player.trim()) {
      toast.error("Who's the player?");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leadingstats", f);
      setLeadingstats((ss) => [data, ...ss]);
      setF(blank);
      toast.success("Stat added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/leadingstats/${id}`);
      setLeadingstats((ss) => ss.filter((s) => s.id !== id));
      toast.success("Stat removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const groups = {};
  leadingstats.forEach((s) => {
    const k = s.stat_type || "Stats";
    (groups[k] = groups[k] || []).push(s);
  });

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <h2 className="font-display text-xl font-bold">Leading stats</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Add players and their numbers — the public page groups them by category and ranks them
          automatically. Tip: use the same team names as the points table so everything matches.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Player</label>
            <input value={f.player} onChange={set("player")} placeholder="e.g. J. Mensah" className={`mt-2 ${inputCls}`} data-testid="admin-stat-player" />
          </div>
          <div>
            <label className={labelCls}>Team (match the points table names)</label>
            <input value={f.team} onChange={set("team")} list="standings-teams" placeholder="e.g. Morbi Rovers" className={`mt-2 ${inputCls}`} data-testid="admin-stat-team" />
            <datalist id="standings-teams">
              {standings.map((s) => (
                <option key={s.id} value={s.team.replace(/^Sample:\s*/, "")} />
              ))}
            </datalist>
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <input value={f.stat_type} onChange={set("stat_type")} list="stat-types" className={`mt-2 ${inputCls}`} data-testid="admin-stat-type" />
            <datalist id="stat-types">
              <option value="Goals" />
              <option value="Assists" />
              <option value="Clean sheets" />
              <option value="Appearances" />
            </datalist>
          </div>
          <div>
            <label className={labelCls}>Number</label>
            {numField(f.value, (v) => setF((x) => ({ ...x, value: v })), "admin-stat-value", "0")}
          </div>
        </div>
        <button onClick={add} data-testid="admin-add-stat-btn" disabled={busy} className="mt-5 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Add stat"}
        </button>
      </div>

      <div className="space-y-3">
        {leadingstats.length === 0 && <p className="text-sm text-muted-foreground">No stats yet.</p>}
        {Object.entries(groups).map(([type, entries]) => (
          <div key={type} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame font-semibold">{type}</p>
            <ul className="mt-2 space-y-1.5">
              {[...entries].sort((a, b) => b.value - a.value).map((s, i) => (
                <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="font-semibold">{i + 1}. {s.player}</span>
                    {s.team && <span className="text-muted-foreground"> · {s.team}</span>}
                  </span>
                  <span className="font-mono text-xs">{s.value}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {leadingstats.length > 0 && (
          <div className="flex flex-wrap justify-end gap-4">
            {leadingstats.map((s) => (
              <button key={s.id} onClick={() => del(s.id)} data-testid="admin-delete-stat" className="inline-flex items-center gap-1.5 text-xs font-semibold text-flame">
                <Trash2 className="h-3.5 w-3.5" /> Remove {s.player}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DataTab = ({ bundles }) => {
  const exportCSV = (bundle) => {
    const { survey, responses } = bundle;
    const header = ["submitted_at", ...survey.questions.map((q) => q.text)];
    const rows = responses.map((r) => [
      r.created_at,
      ...survey.questions.map((q) => {
        const a = (r.answers || []).find((x) => x.question_id === q.id);
        return a ? String(a.value) : "";
      }),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`))
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${survey.title.replace(/\s+/g, "-").toLowerCase()}-responses.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded — opens in Excel or Google Sheets");
  };

  if (bundles.length === 0) {
    return (
      <div className={cardCls}>
        <p className="text-sm text-muted-foreground">No surveys set up yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {bundles.map((b) => {
        const { survey, responses } = b;
        return (
          <div key={survey.id} className={cardCls} data-testid="admin-survey-bundle">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-xl font-bold">{survey.title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {responses.length} {responses.length === 1 ? "response" : "responses"} so far
                </p>
              </div>
              <button
                onClick={() => exportCSV(b)}
                data-testid="admin-export-csv-btn"
                disabled={responses.length === 0}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border-2 border-pitch/25 dark:border-turf/40 text-sm font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf disabled:opacity-40 transition-all"
              >
                <Download className="h-4 w-4" /> Download CSV
              </button>
            </div>

            <div className="mt-6 space-y-6">
              {survey.questions.map((q, qi) => {
                if (q.type !== "choice") {
                  return (
                    <div key={q.id}>
                      <p className="font-semibold text-sm">
                        {qi + 1}. {q.text}
                      </p>
                      <ul className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-2">
                        {responses.map((r) => {
                          const a = (r.answers || []).find((x) => x.question_id === q.id);
                          return a && a.value ? (
                            <li key={r.id + q.id} className="text-sm text-muted-foreground border-l-2 border-sage pl-3">
                              "{a.value}"
                            </li>
                          ) : null;
                        })}
                      </ul>
                    </div>
                  );
                }
                const counts = q.options.map(
                  (o) => responses.filter((r) => (r.answers || []).some((a) => a.question_id === q.id && a.value === o)).length
                );
                const max = Math.max(1, ...counts);
                return (
                  <div key={q.id}>
                    <p className="font-semibold text-sm">
                      {qi + 1}. {q.text}
                    </p>
                    <div className="mt-2 space-y-2">
                      {q.options.map((o, oi) => (
                        <div key={o} className="flex items-center gap-3 text-sm">
                          <span className="w-48 sm:w-64 shrink-0 truncate text-muted-foreground">{o}</span>
                          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-pitch dark:bg-turf rounded-full transition-all"
                              style={{ width: `${(counts[oi] / max) * 100}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs w-8 text-right">{counts[oi]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MessagesTab = ({ messages, setMessages }) => {
  const del = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((ms) => ms.filter((m) => m.id !== id));
      toast.success("Message removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      {messages.length === 0 && (
        <div className={cardCls}>
          <p className="text-sm text-muted-foreground">No messages yet — they'll land here when someone writes to you.</p>
        </div>
      )}
      {messages.map((m) => (
        <div key={m.id} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4" data-testid="admin-message-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-sm">
                {m.name} <span className="font-normal text-muted-foreground">· {m.email}</span>
              </p>
              <p className="text-xs font-mono text-muted-foreground mt-0.5">
                {new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            <button onClick={() => del(m.id)} data-testid="admin-delete-message" aria-label="Delete message" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
};

const emptyQuestion = () => ({ text: "", options: ["", "", "", ""], correct: 0 });

const QuizTab = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState(Array.from({ length: 10 }, emptyQuestion));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/quizzes");
      setQuizzes(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setQ = (i, k, v) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)));
  const setOpt = (i, j, v) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, options: q.options.map((o, jx) => (jx === j ? v : o)) } : q)));

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setQuestions(Array.from({ length: 10 }, emptyQuestion));
  };

  const save = async () => {
    const filled = questions
      .map((q) => ({ text: q.text.trim(), options: q.options.map((o) => o.trim()).filter(Boolean), correct: q.correct }))
      .filter((q) => q.text);
    if (!title.trim()) {
      toast.error("Give the quiz a title.");
      return;
    }
    if (filled.length === 0) {
      toast.error("Write at least one question.");
      return;
    }
    if (filled.some((q) => q.options.length < 2 || q.correct >= q.options.length)) {
      toast.error("Every question needs at least 2 options and a ticked correct answer.");
      return;
    }
    setBusy(true);
    try {
      const payload = { title: title.trim(), questions: filled };
      if (editingId) {
        await api.put(`/admin/quizzes/${editingId}`, payload);
        toast.success("Quiz updated");
      } else {
        await api.post("/admin/quizzes", payload);
        toast.success("Quiz posted — tap Activate to make it live for the kids");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const edit = (q) => {
    setEditingId(q.id);
    setTitle(q.title);
    setQuestions(q.questions.map((x) => ({ text: x.text, options: x.options.length ? [...x.options] : ["", ""], correct: x.correct })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggle = async (id) => {
    try {
      const { data } = await api.post(`/admin/quizzes/${id}/toggle`);
      toast.success(data.active ? "Quiz is live — kids can play it now" : "Quiz paused");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/quizzes/${id}`);
      toast.success("Quiz deleted");
      if (editingId === id) resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardCls}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-bold">{editingId ? "Edit quiz" : "Post the daily quiz"}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Ten questions for every age group. Ticking the circle marks the right answer — kids
              get right/wrong instantly, and the best name tops the leaderboard.
            </p>
          </div>
          {editingId && (
            <button onClick={resetForm} data-testid="admin-quiz-cancel-edit" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              + New quiz instead
            </button>
          )}
        </div>

        <div className="mt-5">
          <label className={labelCls}>Quiz title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Morbi quiz #2 — World Cup special"
            className={`mt-2 ${inputCls}`}
            data-testid="admin-quiz-title-input"
          />
        </div>

        <div className="mt-5 space-y-4">
          {questions.map((q, i) => (
            <div key={i} className="rounded-2xl border-2 border-foreground/10 p-4" data-testid={`admin-quiz-q-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Question {i + 1}</p>
                {questions.length > 1 && (
                  <button onClick={() => setQuestions((qs) => qs.filter((_, idx) => idx !== i))} aria-label={`Remove question ${i + 1}`} className="h-7 w-7 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <input
                value={q.text}
                onChange={(e) => setQ(i, "text", e.target.value)}
                placeholder="e.g. How many players are on the pitch per team?"
                className={`mt-2 ${inputCls}`}
                data-testid={`admin-quiz-q-${i}-text`}
              />
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {q.options.map((opt, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${i}`}
                      checked={q.correct === j}
                      onChange={() => setQ(i, "correct", j)}
                      aria-label={`Mark option ${j + 1} of question ${i + 1} correct`}
                      className="h-4 w-4 accent-[#1E4D2B] shrink-0"
                      data-testid={`admin-quiz-q-${i}-correct-${j}`}
                    />
                    <input
                      value={opt}
                      onChange={(e) => setOpt(i, j, e.target.value)}
                      placeholder={`Option ${j + 1}`}
                      className={`w-full rounded-xl border-2 border-foreground/10 bg-background px-3 py-2 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf`}
                      data-testid={`admin-quiz-q-${i}-opt-${j}`}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Tick the circle next to the correct option.</p>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setQuestions((qs) => [...qs, emptyQuestion()])}
            data-testid="admin-quiz-add-question"
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border-2 border-foreground/10 text-sm font-semibold hover:bg-muted transition-colors"
          >
            <Plus className="h-4 w-4" /> Add another question
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="admin-quiz-save-btn"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save changes" : "Post quiz"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {quizzes.length === 0 && <p className="text-sm text-muted-foreground">No quizzes posted yet — write your first one above.</p>}
        {quizzes.map((q, i) => (
          <div key={q.id} data-testid={`admin-quiz-row-${i}`} className="rounded-2xl bg-card border border-foreground/10 px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                {q.title}
                {q.active ? (
                  <span className="rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="admin-quiz-live-badge">Live now</span>
                ) : (
                  <span className="rounded-full bg-foreground/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Paused</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{q.question_count} questions · {q.entries} {q.entries === 1 ? "entry" : "entries"} on the leaderboard</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => toggle(q.id)} data-testid={`admin-quiz-toggle-${i}`} title={q.active ? "Pause quiz" : "Make live"} className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted transition-colors">
                {q.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <button onClick={() => edit(q)} data-testid={`admin-quiz-edit-${i}`} title="Edit quiz" className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted transition-colors">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => del(q.id)} data-testid={`admin-quiz-delete-${i}`} title="Delete quiz" className="h-9 w-9 grid place-items-center rounded-full hover:bg-flame-soft hover:text-flame transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Admin;
